import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryApi } from '@/lib/api/inventory';
import { locationsApi } from '@/lib/api/locations';
import { useDialog } from '@/hooks/useDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useUndoHistory } from '@/contexts/undo-history-context';
import { Dialog } from '@/components/ui/dialog';
import type { InventoryItem, InventoryQueryParams, Location, InventoryStatus } from '@/types/inventory';
import type { BulkSettableStatus } from '@/types/inventory';
import { BulkSetStatusControl } from '../components/bulk-set-status-control';
import { InventoryStatusBadge } from '../components/inventory-status-badge';

type ActiveTab = 'inventory' | 'needs-home';

export function InventoryPage() {
  const navigate = useNavigate();
  const { dialog, closeDialog, showSuccess, showWarning, showError, showConfirm } = useDialog();
  const { push } = useUndoHistory();
  const [activeTab, setActiveTab] = useState<ActiveTab>('inventory');
  const [locations, setLocations] = useState<Location[]>([]);

  // ── Inventory tab state ────────────────────────────────────────────────────
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [locationFilter, setLocationFilter] = useState<string>('');
  const [sortId1Input, setSortId1Input] = useState('');
  const [sortId2Input, setSortId2Input] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [manifestNameInput, setManifestNameInput] = useState('');
  const [searchBarcodeInput, setSearchBarcodeInput] = useState('');

  const sortId1Filter = useDebounce(sortId1Input, 400);
  const sortId2Filter = useDebounce(sortId2Input, 400);
  const manifestNameFilter = useDebounce(manifestNameInput, 400);
  // Trim before debouncing so "abc" and "abc " settle on one value (one query), and a
  // whitespace-only box is falsy here instead of a round trip the server just trims away.
  const searchBarcode = useDebounce(searchBarcodeInput.trim(), 400);

  // ── Needs a Home tab state ─────────────────────────────────────────────────
  const [needsHomeItems, setNeedsHomeItems] = useState<InventoryItem[]>([]);
  const [needsHomeTotal, setNeedsHomeTotal] = useState(0);
  const [needsHomeLoaded, setNeedsHomeLoaded] = useState(false);
  const [isLoadingNeedsHome, setIsLoadingNeedsHome] = useState(false);
  const [needsHomeError, setNeedsHomeError] = useState<string | null>(null);
  const [needsHomeSearchInput, setNeedsHomeSearchInput] = useState('');
  // Debounced like the inventory tab's box: each keystroke here refetches the whole
  // needs-home list, so an undebounced input meant one full request per character.
  const needsHomeSearch = useDebounce(needsHomeSearchInput.trim(), 400);

  // ── Auto-refresh ───────────────────────────────────────────────────────────
  const [autoRefresh, setAutoRefresh] = useState(true);

  // ── Bulk actions ───────────────────────────────────────────────────────────
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [bulkSortId1, setBulkSortId1] = useState('');
  const [bulkSortId2, setBulkSortId2] = useState('');
  const [bulkLocationId, setBulkLocationId] = useState('');
  const [bulkTimestampOverride, setBulkTimestampOverride] = useState('');
  const [bulkStatus, setBulkStatus] = useState<BulkSettableStatus | ''>('');
  const [bulkSettingStatus, setBulkSettingStatus] = useState(false);

  // ── Sorting state ─────────────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // ── Inline timestamp editing ─────────────────────────────────────────────
  const [editingTimestampId, setEditingTimestampId] = useState<number | null>(null);
  const [editingTimestampValue, setEditingTimestampValue] = useState('');

  // ── Stale-response guards ─────────────────────────────────────────────────
  const inventoryLoadIdRef = useRef(0);
  const needsHomeLoadIdRef = useRef(0);
  // Row counts mirrored into refs so the loaders can ask "is anything on screen right now?"
  // without taking the item arrays as dependencies, which would re-create the loaders on
  // every fetch and re-trigger the effects that call them.
  const inventoryItemCountRef = useRef(0);
  const needsHomeItemCountRef = useRef(0);
  // Which tab is on screen, for loaders that must not touch state owned by the other one.
  const activeTabRef = useRef<ActiveTab>('inventory');

  // ── Derived display values ─────────────────────────────────────────────────
  const isInitialLoad = !inventoryLoaded || !needsHomeLoaded;
  const pageSize = 20;
  const totalPages = Math.ceil(inventoryTotal / pageSize);
  // The 400ms debounce plus a round trip means the last keystroke and the first sign of
  // activity are far apart. Without this the table sits showing stale rows and a stale count
  // with no spinner, which reads as "the typing did nothing" — so operators retype and
  // restart the debounce. Covers the gap before the request has even left the browser.
  const isSearchPending = searchBarcodeInput.trim() !== searchBarcode;
  const isInventoryBusy = isLoadingInventory || isSearchPending;

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const SortHeader = ({ column, label }: { column: string; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
      onClick={() => handleSort(column)}
    >
      {label}
      {sortBy === column ? (
        <span className="ml-1">{sortDirection === 'asc' ? '▲' : '▼'}</span>
      ) : (
        <span className="ml-1 text-gray-600">⇅</span>
      )}
    </th>
  );

  // ── Data loaders ───────────────────────────────────────────────────────────
  const loadLocations = async () => {
    try {
      const data = await locationsApi.getAll();
      setLocations(data ?? []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadInventoryTab = useCallback(async (background = false) => {
    const loadId = ++inventoryLoadIdRef.current;
    try {
      if (!background) setIsLoadingInventory(true);
      // Barcode search is a server-side filter like any other — never fetch an
      // oversized page and filter client-side, or matches beyond that page are invisible.
      const params: InventoryQueryParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
      };
      if (searchBarcode) params.barcodeSearch = searchBarcode;
      if (locationFilter) params.locationId = parseInt(locationFilter);
      if (sortId1Filter) params.sortId1 = sortId1Filter;
      if (sortId2Filter) params.sortId2 = sortId2Filter;
      if (statusFilter) params.status = statusFilter as InventoryStatus;
      if (manifestNameFilter) params.manifestName = manifestNameFilter;
      if (sortBy) params.sortBy = sortBy;
      params.sortDirection = sortDirection;

      const data = await inventoryApi.getInventory(params);
      if (inventoryLoadIdRef.current !== loadId) return;
      const items = data?.items ?? [];
      const total = data?.totalCount ?? 0;

      // Stranded-page correction. Filter handlers reset to page 1, but the 30s background
      // refresh does not — so if the result set shrinks while the operator sits on page 5,
      // the server answers with a real total and an empty page. That renders as
      // "N items total" above "No inventory items found", which is indistinguishable from
      // the search being broken and is exactly how this bug gets re-reported. Correct it
      // here, off the response, rather than in an effect watching derived state: this fires
      // only on a genuine divergence and cannot feed back on itself.
      const lastPage = Math.max(Math.ceil(total / pageSize), 1);
      if (items.length === 0 && total > 0 && currentPage > lastPage) {
        // Retire this load's id so `finally` skips it: the page change below is guaranteed to
        // issue a corrective fetch, and without this the spinner would go out and the previous
        // page's rows would paint for a frame under the already-corrected page number.
        inventoryLoadIdRef.current++;
        setInventoryError(null);
        // Functional so the correction is idempotent and can never move the page forward past
        // a navigation the operator made while this response was in flight.
        setCurrentPage((p) => (p > lastPage ? lastPage : p));
        return;
      }

      setInventoryItems(items);
      setInventoryTotal(total);
      setInventoryError(null);
    } catch (error) {
      if (inventoryLoadIdRef.current !== loadId) return;
      console.error('Error loading inventory:', error);
      // A silent refresh must fail silently. Wiping the table on a background tick would
      // destroy a result set and a multi-row selection the operator built up, for a request
      // they never asked for. Only a foreground load clears state: there, leaving stale rows
      // on screen reads identically to "the search matched everything", and the next click
      // could be a bulk remove against rows that were never searched for.
      // "Showing last known results" is only honest if there are any. If the table is already
      // empty from a failed foreground load, a background tick failing again must not downgrade
      // the message to a reassuring one over an empty table.
      if (background && inventoryItemCountRef.current > 0) {
        setInventoryError('Could not refresh. Showing last known results.');
        return;
      }
      setInventoryItems([]);
      setInventoryTotal(0);
      setSelectedItems(new Set());
      setInventoryError('Could not load inventory. Check your connection and retry.');
    } finally {
      if (inventoryLoadIdRef.current === loadId) {
        setIsLoadingInventory(false);
        setInventoryLoaded(true);
      }
    }
  }, [currentPage, locationFilter, sortId1Filter, sortId2Filter, statusFilter, manifestNameFilter, searchBarcode, sortBy, sortDirection]);

  const loadNeedsHomeTab = useCallback(async (background = false) => {
    // Same stale-response guard the inventory loader has. Without it this was
    // last-*response*-wins across an unbounded per-keystroke fan-out: the response for "A"
    // landing after the response for "AB" would grow the table back under a search box
    // reading "AB", and the next action on this tab is a bulk delete.
    const loadId = ++needsHomeLoadIdRef.current;
    try {
      if (!background) setIsLoadingNeedsHome(true);
      const allItems = await inventoryApi.getNeedsHome() ?? [];
      if (needsHomeLoadIdRef.current !== loadId) return;
      const filtered = needsHomeSearch
        ? allItems.filter(item =>
            item.barcodeValue.toLowerCase().includes(needsHomeSearch.toLowerCase())
          )
        : allItems;
      setNeedsHomeItems(filtered);
      setNeedsHomeTotal(filtered.length);
      setNeedsHomeError(null);
    } catch (error) {
      if (needsHomeLoadIdRef.current !== loadId) return;
      console.error('Error loading needs home:', error);
      // Errors were swallowed entirely here, so a failed reload after a delete left the
      // deleted rows on screen — selectable and deletable a second time.
      if (background && needsHomeItemCountRef.current > 0) {
        setNeedsHomeError('Could not refresh. Showing last known results.');
        return;
      }
      setNeedsHomeItems([]);
      setNeedsHomeTotal(0);
      // selectedItems is shared by both tabs, so only clear it if this tab is the one on
      // screen — otherwise a failed background refresh here would silently wipe a selection
      // the operator built up over on the inventory tab.
      if (activeTabRef.current === 'needs-home') setSelectedItems(new Set());
      setNeedsHomeError('Could not load items. Check your connection and retry.');
    } finally {
      if (needsHomeLoadIdRef.current === loadId) {
        setIsLoadingNeedsHome(false);
        setNeedsHomeLoaded(true);
      }
    }
  }, [needsHomeSearch]);

  // Always-current handles on the loaders. Bulk handlers call reload() *after* an await,
  // by which point the closure they captured belongs to an earlier render and carries that
  // render's filters. Because the stale-response guard is last-issued-wins, such a request
  // gets the highest id and its unfiltered result overwrites the search the operator has
  // typed in the meantime — the box reads a barcode while the table shows everything.
  // Reading the loader from a ref at call time keeps issue order equal to intent order.
  const loadInventoryTabRef = useRef(loadInventoryTab);
  const loadNeedsHomeTabRef = useRef(loadNeedsHomeTab);
  useLayoutEffect(() => {
    loadInventoryTabRef.current = loadInventoryTab;
    loadNeedsHomeTabRef.current = loadNeedsHomeTab;
    inventoryItemCountRef.current = inventoryItems.length;
    needsHomeItemCountRef.current = needsHomeItems.length;
    activeTabRef.current = activeTab;
  });

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadLocations();
  }, []);

  // Clear selection when switching tabs (no page reset — that would re-trigger loadInventoryTab)
  useEffect(() => {
    setSelectedItems(new Set());
  }, [activeTab]);

  // Selection is per-page: the rows a bulk action applies to must be the rows on screen.
  // Search used to return every match in one 1000-row page, so this could not drift; now
  // that it paginates for real, leaving ids selected across a page change would let
  // "Remove Items" fire on rows the operator can no longer see.
  useEffect(() => {
    setSelectedItems(new Set());
  }, [currentPage]);

  // Inventory tab: re-fetch when its filters change
  useEffect(() => {
    loadInventoryTab();
  }, [loadInventoryTab]);

  // Needs a Home tab: re-fetch when its search changes
  useEffect(() => {
    loadNeedsHomeTab();
  }, [loadNeedsHomeTab]);

  // Auto-refresh every 30 seconds (both tabs stay fresh, silently). Depending on the loaders
  // directly would tear down and restart the timer on every keystroke and page change, so an
  // operator working continuously would never actually get a refresh — "30s" only held once
  // they stopped touching the page. The refs keep the timer stable and still current.
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      loadInventoryTabRef.current(true);
      loadNeedsHomeTabRef.current(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Switch tab and silently refresh the newly-visible tab in the background
  const switchToTab = useCallback((tab: ActiveTab) => {
    setActiveTab(tab);
    if (tab === 'inventory') loadInventoryTab(true);
    else loadNeedsHomeTab(true);
  }, [loadInventoryTab, loadNeedsHomeTab]);

  // ── Bulk action handlers ───────────────────────────────────────────────────
  // Called after an await in every bulk handler — must read the loaders from refs, not from
  // this render's closure. See the ref declarations above.
  const reload = useCallback(() => {
    loadInventoryTabRef.current();
    loadNeedsHomeTabRef.current();
  }, []);

  const handleBulkDeleteNeedsHome = () => {
    if (selectedItems.size === 0) return;
    const ids = Array.from(selectedItems);
    const items = needsHomeItems.filter((i) => ids.includes(i.id));
    showConfirm('Delete Items', `Delete ${ids.length} item(s)? You can undo this from the history menu.`, async () => {
      try {
        await Promise.all(ids.map(id => inventoryApi.deleteItem(id)));
        setSelectedItems(new Set());
        loadNeedsHomeTabRef.current();
        const label = ids.length === 1
          ? items[0]?.barcodeValue ?? `Item #${ids[0]}`
          : `${ids.length} inventory items`;
        const sublabel = ids.length === 1 ? 'Needs a Home item deleted' : 'Needs a Home items deleted';
        push(
          label,
          () => Promise.all(ids.map(id => inventoryApi.restoreItem(id))).then(() => {}),
          sublabel,
          // Invoked from the undo menu arbitrarily later — must not close over this render.
          () => loadNeedsHomeTabRef.current()
        );
      } catch {
        showError('Error', 'Failed to delete items');
      }
    }, 'Delete');
  };

  const handleBulkAssignSort = async () => {
    if (selectedItems.size === 0) { showError('No Selection', 'Please select items first'); return; }
    if (!bulkSortId1 && !bulkSortId2) { showError('Invalid Input', 'Please enter at least one Sort ID'); return; }
    try {
      await inventoryApi.bulkAssignSort(Array.from(selectedItems), bulkSortId1 || undefined, bulkSortId2 || undefined);
      setSelectedItems(new Set());
      setBulkSortId1('');
      setBulkSortId2('');
      reload();
      showSuccess('Success', `Updated ${selectedItems.size} items`);
    } catch {
      showError('Error', 'Failed to update items');
    }
  };

  const handleBulkSetStatus = () => {
    if (selectedItems.size === 0 || !bulkStatus || bulkSettingStatus) return;
    // Capture at click time so the confirm and success text match what was sent.
    const ids = Array.from(selectedItems);
    const status = bulkStatus;
    showConfirm('Set Status', `Set status to "${status}" for ${ids.length} item(s)?`, async () => {
      setBulkSettingStatus(true);
      try {
        const { message, skippedCount } = await inventoryApi.bulkSetStatus(ids, status);
        setSelectedItems(new Set());
        setBulkStatus('');
        reload();
        // Coils on an order are skipped, not refused; the message names them.
        if (skippedCount > 0) showWarning('Status Partly Updated', message ?? `Some selected items were skipped because they are on an order`);
        else showSuccess('Success', message || `Status updated for ${ids.length} items`);
      } catch (error) {
        // The server's refusal names the barcodes that blocked the change; show it verbatim.
        const message =
          (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to update status';
        showError('Could Not Set Status', message);
      } finally {
        setBulkSettingStatus(false);
      }
    }, 'Set Status');
  };

  const handleBulkRemove = () => {
    if (selectedItems.size === 0) return;
    showConfirm('Remove Items', `Mark ${selectedItems.size} item(s) as Removed? This cannot be undone.`, async () => {
      try {
        await inventoryApi.bulkRemove(Array.from(selectedItems));
        setSelectedItems(new Set());
        reload();
        showSuccess('Success', `${selectedItems.size} items marked as removed`);
      } catch {
        showError('Error', 'Failed to remove items');
      }
    }, 'Remove');
  };

  const handleBulkClearLocation = () => {
    if (selectedItems.size === 0) return;
    showConfirm('Clear Assignment', `Clear location and sort IDs from ${selectedItems.size} item(s)?`, async () => {
      try {
        await inventoryApi.bulkClearLocation(Array.from(selectedItems));
        setSelectedItems(new Set());
        reload();
        showSuccess('Success', `Assignment cleared from ${selectedItems.size} items`);
      } catch {
        showError('Error', 'Failed to clear assignment');
      }
    }, 'Clear');
  };

  const handleBulkAssignLocation = async () => {
    if (selectedItems.size === 0) { showError('No Selection', 'Please select items first'); return; }
    if (!bulkLocationId) { showError('No Location', 'Please select a location'); return; }
    const locationId = parseInt(bulkLocationId);
    const timestamp = bulkTimestampOverride ? new Date(bulkTimestampOverride).toISOString() : undefined;
    const ids = Array.from(selectedItems);
    // allSettled (not all) so one refusal — e.g. a flagged item the server rejects with
    // 400 — does not fail-fast and silently hide the outcome of the other items. Report
    // how many were assigned vs refused, and surface the server's refusal reason.
    const results = await Promise.allSettled(
      ids.map(id => inventoryApi.assignLocation(id, locationId, timestamp))
    );
    const refused = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    const assigned = ids.length - refused.length;
    setSelectedItems(new Set());
    setBulkLocationId('');
    setBulkTimestampOverride('');
    reload();
    if (refused.length === 0) {
      showSuccess('Success', `Assigned location to ${assigned} items`);
    } else {
      // De-duplicate distinct refusal reasons so a mix (e.g. one flagged, one bad location)
      // isn't collapsed into just the first item's message.
      const reasons = [
        ...new Set(
          refused.map(
            r =>
              (r.reason as { response?: { data?: { message?: string } } })?.response?.data
                ?.message || 'Failed to assign location'
          )
        ),
      ].join('; ');
      showError(
        'Some items not assigned',
        `Assigned ${assigned} of ${ids.length}. ${refused.length} refused: ${reasons}`
      );
    }
  };

  const handleEditTimestamp = (id: number, currentValue?: string) => {
    setEditingTimestampId(id);
    setEditingTimestampValue(currentValue ? new Date(currentValue).toISOString().slice(0, 19) : '');
  };

  const handleSaveTimestamp = async () => {
    if (editingTimestampId === null) return;
    try {
      const value = editingTimestampValue ? new Date(editingTimestampValue).toISOString() : null;
      await inventoryApi.updateTimestamp(editingTimestampId, value);
      setEditingTimestampId(null);
      reload();
      showSuccess('Success', 'Timestamp updated');
    } catch {
      showError('Error', 'Failed to update timestamp');
    }
  };

  const toggleItemSelection = (id: number) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) newSelection.delete(id);
    else newSelection.add(id);
    setSelectedItems(newSelection);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isInitialLoad) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Inventory Management</h1>
        <label className="flex items-center text-sm">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="mr-2"
          />
          Auto-refresh (30s)
        </label>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-700">
        <button
          onClick={() => switchToTab('inventory')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'inventory'
              ? 'border-blue-500 text-blue-400'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Inventory
          {inventoryLoaded && (
            <span className="ml-2 text-xs text-gray-500">({inventoryTotal})</span>
          )}
        </button>
        <button
          onClick={() => switchToTab('needs-home')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'needs-home'
              ? 'border-yellow-500 text-yellow-400'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Needs a Home
          {needsHomeLoaded && needsHomeTotal > 0 && (
            <span className={`ml-2 text-xs font-semibold ${activeTab === 'needs-home' ? 'text-yellow-400' : 'text-yellow-500'}`}>
              ({needsHomeTotal})
            </span>
          )}
        </button>
      </div>

      {/* Inventory tab controls — always in DOM, hidden when inactive */}
      <div className={activeTab !== 'inventory' ? 'hidden' : ''}>
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          {/* Every filter resets the page here in its own handler rather than in an effect.
              An effect resets *after* the fetch effect has already fired with the stale page
              number, so a filter changed while on page 2+ sent a wasted request — and each
              one costs a full table scan on the server. */}
          <input
            type="text"
            value={searchBarcodeInput}
            onChange={(e) => { setSearchBarcodeInput(e.target.value); setCurrentPage(1); }}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
            placeholder="Search by barcode..."
            aria-label="Search inventory by barcode"
            data-testid="inventory-barcode-search"
          />
        </div>
        <div className="bg-gray-800 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Filters</h2>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="font-medium text-gray-300">Status legend:</span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                <span><span className="text-white font-medium">Active</span> — in stock at location</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                <span><span className="text-white font-medium">Picked</span> — pulled for an order</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                <span><span className="text-white font-medium">Removed</span> — taken out of inventory</span>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <select
                value={locationFilter}
                onChange={(e) => { setLocationFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.code}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Manifest / Barge</label>
              <input
                type="text"
                value={manifestNameInput}
                onChange={(e) => { setManifestNameInput(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Filter by manifest name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sort ID 1</label>
              <input
                type="text"
                value={sortId1Input}
                onChange={(e) => { setSortId1Input(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Filter by Sort ID 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sort ID 2</label>
              <input
                type="text"
                value={sortId2Input}
                onChange={(e) => { setSortId2Input(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Filter by Sort ID 2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Picked">Picked</option>
                <option value="Removed">Removed</option>
                <option value="Damaged">Damaged</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Needs a Home tab controls — always in DOM, hidden when inactive */}
      <div className={activeTab !== 'needs-home' ? 'hidden' : ''}>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
            <p className="text-sm text-gray-300">
              Items scanned in from manifests that have not yet been assigned a warehouse location.
            </p>
          </div>
          <input
            type="text"
            value={needsHomeSearchInput}
            onChange={(e) => setNeedsHomeSearchInput(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-yellow-500"
            placeholder="Search by barcode..."
            aria-label="Search items awaiting assignment by barcode"
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.size > 0 && (
        <div className="bg-blue-900 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Bulk Actions ({selectedItems.size} selected)</span>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded transition-colors text-sm"
            >
              Clear selection
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Location */}
            <div className="flex items-center gap-2 bg-blue-950/50 rounded px-3 py-2">
              <select
                value={bulkLocationId}
                onChange={(e) => setBulkLocationId(e.target.value)}
                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Location...</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.code}</option>
                ))}
              </select>
              <input
                type="datetime-local"
                step="1"
                value={bulkTimestampOverride}
                onChange={(e) => setBulkTimestampOverride(e.target.value)}
                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500"
                title="Override timestamp (leave blank for current time)"
                placeholder="Override time..."
              />
              <button
                onClick={handleBulkAssignLocation}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded transition-colors text-sm whitespace-nowrap"
              >
                Assign Location
              </button>
            </div>
            {/* Sort IDs */}
            <div className="flex items-center gap-2 bg-blue-950/50 rounded px-3 py-2">
              <input
                type="text"
                value={bulkSortId1}
                onChange={(e) => setBulkSortId1(e.target.value)}
                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500 w-28"
                placeholder="Sort ID 1"
              />
              <input
                type="text"
                value={bulkSortId2}
                onChange={(e) => setBulkSortId2(e.target.value)}
                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500 w-28"
                placeholder="Sort ID 2"
              />
              <button
                onClick={handleBulkAssignSort}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm whitespace-nowrap"
              >
                Assign Sort IDs
              </button>
            </div>
            {/* Status + destructive (inventory tab only) */}
            {activeTab === 'inventory' && (
              <>
                <div className="flex items-center gap-2 bg-blue-950/50 rounded px-3 py-2">
                  <BulkSetStatusControl
                    value={bulkStatus}
                    onChange={setBulkStatus}
                    onSubmit={handleBulkSetStatus}
                    isSubmitting={bulkSettingStatus}
                  />
                </div>
                <div className="flex items-center gap-2 bg-blue-950/50 rounded px-3 py-2">
                  <button
                    onClick={handleBulkClearLocation}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 rounded transition-colors text-sm whitespace-nowrap"
                  >
                    Clear Assignment
                  </button>
                  <button
                    onClick={handleBulkRemove}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded transition-colors text-sm whitespace-nowrap"
                  >
                    Remove Items
                  </button>
                </div>
              </>
            )}
            {/* Delete (needs-home tab only) */}
            {activeTab === 'needs-home' && (
              <div className="flex items-center gap-2 bg-blue-950/50 rounded px-3 py-2">
                <button
                  onClick={handleBulkDeleteNeedsHome}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded transition-colors text-sm whitespace-nowrap"
                >
                  Delete Items
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inventory table — always in DOM, hidden when inactive */}
      <div className={`bg-gray-800 rounded-lg overflow-hidden ${activeTab !== 'inventory' ? 'hidden' : ''}`}>
        <div className="px-6 py-3 bg-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-300 flex items-center gap-2">
            {isInventoryBusy && (
              <svg className="animate-spin h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {inventoryTotal} items total
          </span>
          <span className="text-sm text-gray-300">
            Page {currentPage} of {totalPages || 1}
            {inventoryItems.length > 0 && inventoryItems.length < pageSize && (
              <span className="text-gray-500 ml-1">({inventoryItems.length} on this page)</span>
            )}
          </span>
        </div>
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) setSelectedItems(new Set(inventoryItems.map((i) => i.id)));
                    else setSelectedItems(new Set());
                  }}
                  checked={inventoryItems.length > 0 && inventoryItems.every((i) => selectedItems.has(i.id))}
                />
              </th>
              <SortHeader column="barcode" label="Barcode" />
              <SortHeader column="location" label="Location" />
              <SortHeader column="sortid1" label="Sort ID 1" />
              <SortHeader column="sortid2" label="Sort ID 2" />
              <SortHeader column="status" label="Status" />
              <SortHeader column="assignedAt" label="Assigned At" />
              <SortHeader column="manifest" label="Manifest" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {inventoryItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-750">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                  />
                </td>
                <td className="px-4 py-3 font-mono">
                  <button
                    onClick={() => navigate(`/inventory/${item.id}`)}
                    className="text-blue-400 hover:text-blue-300 hover:underline"
                  >
                    {item.barcodeValue}
                  </button>
                </td>
                <td className="px-4 py-3">
                  {item.locationCode || <span className="text-yellow-400">No Location</span>}
                </td>
                <td className="px-4 py-3">{item.sortId1 || '-'}</td>
                <td className="px-4 py-3">{item.sortId2 || '-'}</td>
                <td className="px-4 py-3">
                  <InventoryStatusBadge status={item.status} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {editingTimestampId === item.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="datetime-local"
                        step="1"
                        value={editingTimestampValue}
                        onChange={(e) => setEditingTimestampValue(e.target.value)}
                        className="px-1.5 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs focus:outline-none focus:border-blue-500"
                      />
                      <button onClick={handleSaveTimestamp} className="text-green-400 hover:text-green-300 text-xs px-1">Save</button>
                      <button onClick={() => setEditingTimestampId(null)} className="text-gray-400 hover:text-gray-300 text-xs px-1">Cancel</button>
                    </div>
                  ) : (
                    <span
                      className="cursor-pointer hover:text-blue-400"
                      onClick={(e) => { e.stopPropagation(); handleEditTimestamp(item.id, item.locationAssignedAt); }}
                      title="Click to edit timestamp"
                    >
                      {item.locationAssignedAt
                        ? new Date(item.locationAssignedAt).toLocaleString()
                        : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">{item.manifestBargeName}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {inventoryError ? (
          <div className="text-center py-8 text-red-300">
            <p>{inventoryError}</p>
            <button
              onClick={() => loadInventoryTab()}
              className="mt-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors text-sm"
            >
              Retry
            </button>
          </div>
        ) : inventoryItems.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No inventory items found matching the current filters.
          </div>
        )}
      </div>

      {/* Inventory pagination — always in DOM, hidden when inactive */}
      <div className={totalPages > 1 ? (activeTab !== 'inventory' ? 'hidden' : '') : 'hidden'}>
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Needs a Home table — always in DOM, hidden when inactive */}
      <div className={`bg-gray-800 rounded-lg overflow-hidden ${activeTab !== 'needs-home' ? 'hidden' : ''}`}>
        <div className="px-6 py-3 bg-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-300 flex items-center gap-2">
            {isLoadingNeedsHome && (
              <svg className="animate-spin h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {needsHomeTotal} items awaiting assignment
          </span>
        </div>
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) setSelectedItems(new Set(needsHomeItems.map((i) => i.id)));
                    else setSelectedItems(new Set());
                  }}
                  checked={needsHomeItems.length > 0 && selectedItems.size === needsHomeItems.length}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Barcode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Manifest</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Received</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {needsHomeItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-750">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                  />
                </td>
                <td className="px-4 py-3 font-mono">
                  <button
                    onClick={() => navigate(`/inventory/${item.id}`)}
                    className="text-blue-400 hover:text-blue-300 hover:underline"
                  >
                    {item.barcodeValue}
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">{item.manifestBargeName}</td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {item.receivedTimestamp ? new Date(item.receivedTimestamp).toLocaleString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {needsHomeError ? (
          <div className="text-center py-8 text-red-300">
            <p>{needsHomeError}</p>
            <button
              onClick={() => loadNeedsHomeTabRef.current()}
              className="mt-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors text-sm"
            >
              Retry
            </button>
          </div>
        ) : needsHomeItems.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            All items have been assigned locations.
          </div>
        )}
      </div>

      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
